import {
  ArrowLeftIcon,
  CameraIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  PhotoIcon,
  PlusIcon,
  QrCodeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';

interface Vehicle {
  id: string;
  vin: string;
  stock_number: string | null;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  mileage: number | null;
  exterior_color: string | null;
  asking_price: number | null;
  images: string[];
  status: string;
}

interface VehicleImage {
  id: string;
  file_path: string;
  is_primary: boolean;
  display_order: number;
}

const MobileVehiclePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [images, setImages] = useState<VehicleImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const fetchVehicle = useCallback(async () => {
    try {
      const data = await apiService.get<Vehicle>(`/vehicles/${id}`);
      setVehicle(data);
    } catch (error) {
      console.error('Failed to fetch vehicle:', error);
      toast.error('Failed to load vehicle details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchImages = useCallback(async () => {
    try {
      const data = await apiService.get<VehicleImage[]>(`/vehicles/${id}/vehicle-images`);
      setImages(data || []);
    } catch (error) {
      console.error('Failed to fetch images:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchVehicle();
      fetchImages();
    }
  }, [id, fetchVehicle, fetchImages]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    const totalFiles = files.length;
    let completed = 0;

    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const backendUrl = apiService.getBaseUrl();
        const response = await fetch(`${backendUrl}/api/v1/vehicles/${id}/vehicle-images`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        completed++;
        setUploadProgress(Math.round((completed / totalFiles) * 100));
      } catch (error) {
        console.error('Failed to upload image:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setUploadProgress(0);
    toast.success(`Uploaded ${completed} of ${totalFiles} images`);
    fetchImages();

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCameraCapture = () => {
    // Trigger file input with camera capture
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.click();
    }
  };

  const handleGallerySelect = () => {
    // Trigger file input without camera capture
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  };

  const handlePublishToWebsite = async () => {
    if (!vehicle || !id) return;

    // Check if vehicle has required data
    if (!vehicle.asking_price) {
      toast.error('Please add an asking price before publishing');
      return;
    }

    if (images.length === 0) {
      toast.error('Please add at least one photo before publishing');
      return;
    }

    setPublishing(true);

    try {
      // Update vehicle status to 'available' to make it visible on website
      await apiService.patch(`/vehicles/${id}`, {
        status: 'available',
      });

      // Refresh vehicle data
      await fetchVehicle();

      toast.success('🎉 Vehicle published to website!', {
        duration: 5000,
        icon: '🌐',
      });
    } catch (error) {
      console.error('Failed to publish vehicle:', error);
      toast.error('Failed to publish vehicle. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!vehicle || !id) return;

    setPublishing(true);

    try {
      // Update vehicle status to 'pending' to remove from website
      await apiService.patch(`/vehicles/${id}`, {
        status: 'pending',
      });

      // Refresh vehicle data
      await fetchVehicle();

      toast.success('Vehicle removed from website');
    } catch (error) {
      console.error('Failed to unpublish vehicle:', error);
      toast.error('Failed to update vehicle status');
    } finally {
      setPublishing(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Price TBD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <XMarkIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Vehicle Not Found</h2>
          <p className="text-gray-600 mb-4">
            The vehicle you're looking for doesn't exist or you don't have access.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Combine uploaded images and external images
  const allImages = [
    ...images.map((img) => ({
      url: apiService.getFileUrl(img.file_path),
      isExternal: false,
    })),
    ...(vehicle.images || []).map((url) => ({
      url,
      isExternal: true,
    })),
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Header */}
      <header className="bg-blue-600 text-white sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="text-center flex-1">
            <h1 className="font-semibold text-lg">CarLotPilot Mobile</h1>
          </div>
          <div className="w-10"></div>
        </div>
      </header>

      {/* Vehicle Info Card */}
      <div className="bg-white shadow-sm mx-4 mt-4 rounded-xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h2>
              {vehicle.trim && <p className="text-sm text-gray-600">{vehicle.trim}</p>}
            </div>
            <span
              className={`px-3 py-1 text-xs font-medium rounded-full ${
                vehicle.status === 'available'
                  ? 'bg-green-100 text-green-800'
                  : vehicle.status === 'sold'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {vehicle.status}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500 text-xs">VIN</p>
              <p className="font-mono text-xs break-all">{vehicle.vin}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Stock #</p>
              <p className="font-medium">{vehicle.stock_number || '-'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Mileage</p>
              <p className="font-medium">{vehicle.mileage?.toLocaleString() || '-'} mi</p>
              {/* Publish to Website Button - Prominent if not yet published */}
              {vehicle.status !== 'available' && (
                <button
                  onClick={handlePublishToWebsite}
                  disabled={uploading || publishing}
                  className="w-full mb-3 flex items-center justify-center py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg disabled:opacity-50 disabled:from-gray-400 disabled:to-gray-500"
                >
                  {publishing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Publishing...
                    </>
                  ) : (
                    <>
                      <GlobeAltIcon className="h-6 w-6 mr-2" />
                      Publish to Website
                    </>
                  )}
                </button>
              )}

              {/* Published Status - Show if already published */}
              {vehicle.status === 'available' && (
                <div className="mb-3 flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center text-green-700">
                    <CheckCircleIcon className="h-6 w-6 mr-2" />
                    <span className="font-medium">Live on Website</span>
                  </div>
                  <button
                    onClick={handleUnpublish}
                    disabled={publishing}
                    className="text-sm text-green-700 underline disabled:opacity-50"
                  >
                    {publishing ? 'Removing...' : 'Unpublish'}
                  </button>
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Price</p>
              <p className="font-bold text-blue-600">{formatPrice(vehicle.asking_price)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Photos Section */}
      <div className="bg-white shadow-sm mx-4 mt-4 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <PhotoIcon className="h-5 w-5 mr-2 text-gray-500" />
              Photos ({allImages.length})
            </h3>
          </div>
        </div>

        {/* Image Grid */}
        {allImages.length > 0 ? (
          <div className="p-4 grid grid-cols-3 gap-2">
            {allImages.map((img, index) => (
              <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={img.url}
                  alt={`Vehicle ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="%239CA3AF"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /%3E%3C/svg%3E';
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <PhotoIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No photos yet</p>
            <p className="text-sm text-gray-400">Add photos using the buttons below</p>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 mx-4 w-full max-w-sm">
            <p className="text-center font-medium mb-4">Uploading Photos...</p>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">{uploadProgress}%</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 safe-area-pb">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleCameraCapture}
            disabled={uploading}
            className="flex items-center justify-center py-4 bg-blue-600 text-white rounded-xl font-medium disabled:opacity-50"
          >
            <CameraIcon className="h-6 w-6 mr-2" />
            Take Photo
          </button>
          <button
            onClick={handleGallerySelect}
            disabled={uploading}
            className="flex items-center justify-center py-4 bg-gray-100 text-gray-700 rounded-xl font-medium disabled:opacity-50"
          >
            <PlusIcon className="h-6 w-6 mr-2" />
            From Gallery
          </button>
        </div>

        <button
          onClick={() => navigate(`/mobile/scan?vehicleId=${id}`)}
          className="mt-3 w-full flex items-center justify-center py-4 bg-purple-600 text-white rounded-xl font-medium"
        >
          <QrCodeIcon className="h-6 w-6 mr-2" />
          Scan VIN Barcode
        </button>
      </div>

      {/* Bottom spacer for fixed buttons */}
      <div className="h-48"></div>
    </div>
  );
};

export default MobileVehiclePage;
